import Cookies from "js-cookie";
import { omit } from "lodash";
import { useContext, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";
import { AdminContext } from "../context/AdminContext";
import AdminServices from "../services/AdminServices";
import { notifyError, notifySuccess } from "../utils/toast";
import { sleep } from "../utils";

const useLoginSubmit = () => {
  const newPassword = useRef({});
  const [loading, setLoading] = useState(false);
  const { state, dispatch } = useContext(AdminContext);
  const history = useHistory();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    control,
  } = useForm({
    shouldUnregister: true,
  });

  newPassword.current = watch("newPassword", "");

  const onSubmit = ({ name, email, verifyEmail, password, phone }) => {
    setLoading(true);
    const cookieTimeOut = 0.0007;
    // const cookieTimeOut = 0.5;

    if (location.pathname === "/login") {
      AdminServices.loginAdmin({ email, password })
        .then((res) => {
          if (res) {
            setLoading(false);
            notifySuccess("Login Success!");
            dispatch({ type: "USER_LOGIN", payload: res });
            Cookies.set("adminInfo", JSON.stringify(res), {
              expires: cookieTimeOut,
            });
            history.replace("/");
          }
        })
        .catch((err) => {
          notifyError(err ? err.response.data.message : err.message);
          setLoading(false);
        });
    }

    if (location.pathname === "/signup") {
      AdminServices.registerUser({ name, email, password, phone })
        .then(async (res) => {
          if (res) {
            notifySuccess("Register Success!");
            await sleep(2000);
            window.open("https://pos.digistoreafrica.com", "_self");
            setLoading(false);
            // dispatch({ type: "USER_LOGIN", payload: res });
            // Cookies.set("adminInfo", JSON.stringify(res), {
            //   expires: cookieTimeOut,
            // });
            // history.replace("/");
          }
        })
        .catch((err) => {
          notifyError(err ? err.response.data.message : err.message);
          setLoading(false);
        });
    }

    if (location.pathname === "/forgot-password") {
      AdminServices.forgetPassword({ verifyEmail })
        .then((res) => {
          setLoading(false);
          notifySuccess(res.message);
        })
        .catch((err) => {
          setLoading(false);
          notifyError(err ? err.response.data.message : err.message);
        });
    }
  };

  const getUserLoginDetails = async (
    { username },
    onSuccessStepOne,
    onSuccessStepTwo
  ) => {
    try {
      setLoading(true);
      const data = await AdminServices.checkUserLoginDetails(username);

      const { status, message, has_pwd, has_login_default, uid } = data;

      if (Number(status) !== 0) {
        return notifyError(message);
      }

      if (has_pwd === "NO") {
        return notifySuccess(`No password`);
      }

      dispatch({ type: "USER_UID", payload: uid });
      if (has_login_default) {
        notifySuccess(
          `Setup up password to enable easy access to your account using '${username}'`
        );
        onSuccessStepOne();
      }

      if (!has_login_default) {
        onSuccessStepTwo();
      }

      // setStep(2);
    } catch (err) {
      notifyError(
        err ? err?.response?.data?.message : err?.message || "An error occured"
      );
    } finally {
      setLoading(false);
    }
  };

  const setupUserPIN = async (values) => {
    try {
      setLoading(true);

      const payload = {
        mod_by: values?.username,
        uid: state?.userUID,
        old_pass: values?.password,
        new_pass: values?.newPassword,
      };

      const data = await AdminServices.setupUserPassword(payload);

      const { status, message } = data;

      if (Number(status) !== 0) {
        return notifyError(message);
      }

      notifySuccess(message + " " + "Logging you in...");
      await signInUser({
        username: values?.username,
        password: values?.newPassword,
      });
    } catch (err) {
      notifyError(err ? err.response.data.message : err.message);
    } finally {
      setLoading(false);
    }
  };

  const signInUser = async (values) => {
    try {
      setLoading(true);
      const cookieTimeOut = 0.5;

      let data = await AdminServices.signInUser(values);
      const { status, message } = data;

      if (status && status === "99") {
        notifyError(message);
      }

      if (status && status === "0") {
        if ("sid" in data) {
          notifySuccess("Login Success!");
          dispatch({ type: "USER_LOGIN", payload: data });
          data = omit(data, ["user_permissions"]);
          Cookies.set("adminInfo", JSON.stringify(data), {
            expires: cookieTimeOut,
          });
          return history.replace("/");
        }
        notifyError("Sorry an error occurred");
      }
    } catch (err) {
      notifyError(
        err ? err?.response?.data?.message : err?.message || "An error occured"
      );
    } finally {
      setLoading(false);
    }
  };

  const registerUser = async ({ values, nextAction }) => {
    try {
      setLoading(true);
      let data = await AdminServices.registerUser(values);
      const { status, message } = data;

      if (status && status === "99") {
        return notifyError(message);
      }

      if (status && status === "0") {
        if (message === "Success: User exist") {
          return notifyError("User already exists. Click below to Login");
        }
        notifySuccess(data?.message);
        dispatch({ type: "USER_REGISTER", payload: { ...values } });
        nextAction();
      }
    } catch (err) {
      notifyError(
        err ? err?.response?.data?.message : err?.message || "An error occured"
      );
    } finally {
      setLoading(false);
    }
  };

  const didntReceiveCode = async ({ setTimeLeft }) => {
    try {
      setLoading(true);

      let data = await AdminServices.registerUser({
        contact_phone: state?.userRegisterDetails?.contact_phone,
      });

      if (Number(data?.status) !== 0) {
        return notifyError(data?.message);
      }
      notifySuccess(data?.message);
      setTimeLeft(new Date(new Date().getTime() + 2 * 60000));
    } catch (err) {
      notifyError(
        err ? err?.response?.data?.message : err?.message || "An error occured"
      );
    } finally {
      setLoading(false);
    }
  };

  const validateOTP = async ({ values, nextAction }) => {
    console.log(state);
    try {
      setLoading(true);
      let data = await AdminServices.verifyOTP({
        contact_phone: state?.userRegisterDetails?.contact_phone,
        otp: values?.otp,
      });

      if (Number(data?.status) !== 0) {
        return notifyError(data?.message);
      }

      notifySuccess(data?.message);
      nextAction();
    } catch (err) {
      notifyError(
        err ? err?.response?.data?.message : err?.message || "An error occured"
      );
    } finally {
      setLoading(false);
    }
  };

  const onboardUser = async (values) => {
    try {
      const payload = {
        ...values,
        contact_phone: state?.userRegisterDetails?.contact_phone,
        username: state?.userRegisterDetails?.contact_phone,
        password: "1234567",
        type: "DIGISTORE BUSINESS APP",
        source: "WEBSITE",
      };

      setLoading(true);
      let data = await AdminServices.onboardUser(payload);

      if (Number(data?.status) !== 0) {
        return notifyError(data?.message);
      }

      notifySuccess(data?.message);
      await sleep(2000);
      window.open("https://pos.digistoreafrica.com", "_self");
      setLoading(false);
    } catch (err) {
      notifyError(
        err ? err?.response?.data?.message : err?.message || "An error occured"
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    getUserLoginDetails,
    signInUser,
    setupUserPIN,
    onSubmit,
    register,
    handleSubmit,
    errors,
    loading,
    newPassword,
    setValue,
    registerUser,
    control,
    didntReceiveCode,
    validateOTP,
    onboardUser,
  };
};

export default useLoginSubmit;
